import React, { ReactElement } from 'react';
import clsx from 'clsx';
import { greys, mainColors } from '../../styling/theme';
import { Status } from '../../types/redux/data/dataTypes';
import { Link } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Theme, Tooltip, Typography } from '@mui/material';

interface StyleProps {
  status: keyof typeof mainColors;
  statusDarker: keyof typeof mainColors;
  color: string | null;
  neutralColor?: boolean;
  disabled?: boolean;
  isNA?: boolean;
  /** Narrower padding and content-based width — for dense tables (e.g. overview with many columns). */
  dense?: boolean;
  /** False for deceased pills: no pointer, no hover darken. */
  interactive?: boolean;
}

const pillBackground = (props: StyleProps) => {
  if (props.isNA) return greys.grey200;
  if (props.disabled) return greys.grey700;
  if (props.neutralColor) return mainColors.mainBlue_lighter;
  return mainColors[props.status];
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  PageLinkButtonRoot: {
    backgroundColor: pillBackground,
    color: (props: StyleProps) => {
      if (props.isNA) return greys.grey600;
      return props.color ? props.color : 'white';
    },
    width: (props: StyleProps) => (props.dense ? 'auto' : '100%'),
    maxWidth: (props: StyleProps) => (props.dense ? '8.75rem' : '12.50rem'),
    minWidth: (props: StyleProps) => (props.dense ? 'min-content' : undefined),
    padding: (props: StyleProps) =>
      props.dense ? '0.12rem 0.44rem' : undefined,
    fontSize: (props: StyleProps) => (props.dense ? '0.75rem' : undefined),
    lineHeight: (props: StyleProps) => (props.dense ? 1.25 : undefined),
    whiteSpace: 'nowrap',
    '&.MuiButton-root': {
      cursor: (props: StyleProps) =>
        props.interactive === false ? 'default' : undefined,
    },
    '&:hover': {
      backgroundColor: (props: StyleProps) => {
        if (props.interactive === false) return pillBackground(props);
        if (props.isNA) return greys.grey300;
        if (props.disabled) return greys.grey700;
        if (props.neutralColor) return mainColors.mainBlue;
        return mainColors[props.statusDarker];
      },
    },
  },
  link: {
    textDecoration: 'none',
  },
}));

export interface StatusButtonProps {
  status: Status;
  text?: string;
  textColor?: string | null;
  tooltipText?: string;
  neutralColor?: boolean;
  disabled?: boolean;
  dense?: boolean;
  classes?: {
    root?: string;
    link?: string;
  };
}

export interface PageLinkButtonProps extends StatusButtonProps {
  fundName?: string;
  page: string;
  section: string;
  setTitleBarSelect?: {
    selectedValue: string;
  };
  additionalHandler?: () => void;
}

export function StatusButton({
  status,
  classes: classOverrides,
  ...props
}: StatusButtonProps): ReactElement {
  const classes = useStyles({
    status,
    statusDarker: `${status}_darker`,
    color: props.textColor || null,
    neutralColor: props.neutralColor,
    disabled: props.disabled,
    isNA: status === Status.NA,
    dense: props.dense,
    interactive: false,
  } as StyleProps);

  const button = (
    <Button
      disabled={props.disabled}
      disableRipple
      tabIndex={-1}
      className={clsx(classes.PageLinkButtonRoot, classOverrides?.root)}
      size={props.dense ? 'small' : 'medium'}
    >
      {props.text || status}
    </Button>
  );

  const content = props.tooltipText ? (
    <Tooltip
      title={
        <Typography variant="h4" style={{ color: 'white' }}>
          {props.tooltipText}
        </Typography>
      }
      placement="top"
    >
      {button}
    </Tooltip>
  ) : (
    button
  );

  return (
    <span className={clsx(classes.link, classOverrides?.link)}>{content}</span>
  );
}

function PageLinkButton({
  status,
  section,
  page,
  classes: classOverrides,
  ...props
}: React.PropsWithChildren<PageLinkButtonProps>): ReactElement {
  const handleClick = () => {
    //TODO manage what clicking on this button does
    if (props.additionalHandler) props.additionalHandler();
  };
  const classes = useStyles({
    status,
    statusDarker: `${status}_darker`,
    color: props.textColor || null,
    neutralColor: props.neutralColor,
    disabled: props.disabled,
    isNA: status === Status.NA,
    dense: props.dense,
  } as StyleProps);

  return (
    <Link
      to={`/${section}/${page}${props.fundName ? `?fundId=${props.fundName}` : ``}`}
      className={clsx(classes.link, classOverrides?.link)}
    >
      {props.tooltipText ? (
        <Tooltip
          title={
            <Typography variant="h4" style={{ color: 'white' }}>
              {props.tooltipText}
            </Typography>
          }
          placement="top"
        >
          <Button
            disabled={props.disabled}
            onClick={handleClick}
            className={clsx(classes.PageLinkButtonRoot, classOverrides?.root)}
            size={props.dense ? 'small' : 'medium'}
          >
            {props.text || status}
          </Button>
        </Tooltip>
      ) : (
        <Button
          disabled={props.disabled}
          onClick={handleClick}
          className={clsx(classes.PageLinkButtonRoot, classOverrides?.root)}
          size={props.dense ? 'small' : 'medium'}
        >
          {props.text || status}
        </Button>
      )}
    </Link>
  );
}

export default PageLinkButton;
